import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Shield, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const AdminLogin = () => {
  const navigate = useNavigate();
  const logoUrl = 'https://customer-assets.emergentagent.com/job_transport-app-29/artifacts/c2qih6ut_J%20%26%20S%20Logo%2001%20%20%282%29_1024x1024.jpg';
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast({
        title: "Missing Fields",
        description: "Please enter email and password",
        variant: "destructive"
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/login`, {
        email,
        password
      });
      
      if (response.data.success) {
        // Store admin session
        localStorage.setItem('admin_id', response.data.admin_id);
        localStorage.setItem('admin_email', response.data.email);
        localStorage.setItem('admin_role', response.data.role);
        
        toast({
          title: "Login Successful",
          description: "Welcome to the admin dashboard",
        });
        
        navigate('/admin');
      }
    } catch (error) {
      console.error('Admin login error:', error);
      toast({
        title: "Login Failed",
        description: error.response?.data?.detail || "Invalid credentials",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Button variant="ghost" onClick={() => navigate('/')} className="mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Home
          </Button>
          <img src={logoUrl} alt="Logo" className="h-20 w-20 mx-auto mb-4 object-contain" />
          <h1 className="text-3xl font-bold bg-gradient-to-r from-amber-600 to-yellow-500 bg-clip-text text-transparent mb-2">
            Admin Portal
          </h1>
          <p className="text-gray-600">Secure access for administrators</p>
        </div>

        <Card className="p-8 border-2">
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <Label htmlFor="email">Admin Email</Label>
              <Input 
                id="email"
                type="email"
                placeholder="admin@jstyle.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 border-2"
                data-testid="admin-email"
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input 
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 border-2"
                data-testid="admin-password"
              />
            </div>

            <div className="bg-slate-50 border-2 border-slate-200 rounded-lg p-3">
              <p className="text-xs text-slate-700 flex items-start gap-2">
                <Shield size={14} className="mt-0.5 flex-shrink-0 text-amber-600" />
                <span>This portal is for authorized administrators only. All access is logged and monitored.</span>
              </p>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-800">
                <strong>Demo Credentials:</strong><br/>
                Email: admin@jstyle.com<br/>
                Password: admin123
              </p>
            </div>

            <Button 
              type="submit"
              className="w-full bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white py-6"
              disabled={isLoading}
              data-testid="admin-login-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={16} />
                  Signing in...
                </>
              ) : (
                'Access Admin Dashboard'
              )}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default AdminLogin;
